"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  MediumField,
  type MediumState,
  mediumIndex,
} from "@/components/tools/physics-waves/medium-field";
import { WorkingSteps } from "@/components/tools/physics-waves/quantity-field";
import { RayDiagram } from "@/components/tools/physics-waves/ray-diagram";
import type { SNELLS_LAW_CALCULATOR_STRINGS } from "@/components/tools/snells-law-calculator";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-waves/format";
import {
  brewsterAngle,
  criticalAngle,
  fresnelReflectance,
  MATERIAL_INDEX,
  MATERIAL_ORDER,
  type MaterialId,
  type SnellError,
  type SnellTarget,
  solveSnell,
  speedInMedium,
} from "@/lib/tool-kit/physics-waves/refraction";

export type SnellStrings = typeof SNELLS_LAW_CALCULATOR_STRINGS;

interface PanelProps {
  strings: SnellStrings;
  fmt: NumberFormatter;
  sig: number;
}

const RAD = Math.PI / 180;

function errorText(error: SnellError, strings: SnellStrings): string {
  return strings.errors[error];
}

function angleFormatter(fmt: NumberFormatter, sig: number) {
  return (degrees: number) => `${formatSig(fmt, degrees, sig)}°`;
}

function rayLabels(
  strings: SnellStrings,
  n1: number,
  n2: number,
  theta1: number,
  theta2: number | null,
  fmt: NumberFormatter,
  sig: number
) {
  const angle = angleFormatter(fmt, sig);
  const index = (n: number) => fmt(n, { maximumFractionDigits: 6 });
  return {
    figureLabel: strings.figureLabel,
    figureDescription: strings.figureDescription,
    mediumTop: t(strings.mediumTop, { n: index(n1), angle: angle(theta1) }),
    mediumBottom:
      theta2 === null
        ? t(strings.mediumBottomNone, { n: index(n2) })
        : t(strings.mediumBottom, { n: index(n2), angle: angle(theta2) }),
  };
}

const DEFAULT_MEDIA: [MediumState, MediumState] = [
  { id: "air", custom: "1.2" },
  { id: "water", custom: "1.4" },
];

function RefractionResults({
  n1,
  n2,
  theta1,
  strings,
  fmt,
  sig,
}: PanelProps & { n1: number; n2: number; theta1: number }) {
  const angle = angleFormatter(fmt, sig);
  const outcome = solveSnell("theta2", { n1, n2, theta1 });
  const theta2 = outcome.ok ? outcome.value : null;
  const critical = criticalAngle(n1, n2);
  const reflect = fresnelReflectance(n1, n2, theta1);
  const percent = (value: number) => `${formatSig(fmt, value * 100, sig)}%`;
  const sine = (n1 * Math.sin(theta1 * RAD)) / n2;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.theta2}
          value={theta2 === null ? strings.totalInternal : angle(theta2)}
        />
        {theta2 === null ? null : (
          <StatCard
            label={strings.deviation}
            value={angle(Math.abs(theta1 - theta2))}
          />
        )}
        <StatCard
          label={strings.critical}
          value={critical === null ? strings.noCritical : angle(critical)}
        />
        <StatCard
          label={strings.brewster}
          value={angle(brewsterAngle(n1, n2))}
        />
        <StatCard
          label={strings.reflected}
          value={percent(reflect.unpolarised)}
        />
        <StatCard
          label={strings.transmitted}
          value={percent(1 - reflect.unpolarised)}
        />
        <StatCard
          label={strings.speed1}
          value={`${formatSig(fmt, speedInMedium(n1), sig)} m/s`}
        />
        <StatCard
          label={strings.speed2}
          value={`${formatSig(fmt, speedInMedium(n2), sig)} m/s`}
        />
      </StatGrid>
      <RayDiagram
        labels={rayLabels(strings, n1, n2, theta1, theta2, fmt, sig)}
        n1={n1}
        n2={n2}
        theta1={theta1}
        theta2={theta2}
      />
      <WorkingSteps
        heading={strings.working}
        lines={
          theta2 === null
            ? [
                t(strings.stepTir, {
                  sine: formatSig(fmt, sine, sig),
                  critical: critical === null ? "" : angle(critical),
                }),
              ]
            : [
                t(strings.stepTheta2, {
                  n1: formatSig(fmt, n1, sig),
                  n2: formatSig(fmt, n2, sig),
                  theta1: angle(theta1),
                  sine: formatSig(fmt, sine, sig),
                  theta2: angle(theta2),
                }),
              ]
        }
      />
    </>
  );
}

export function RefractionPanel({ strings, fmt, sig }: PanelProps) {
  const [media, setMedia] = useState(DEFAULT_MEDIA);
  const [angleText, setAngleText] = useState("30");
  const n1 = mediumIndex(media[0]);
  const n2 = mediumIndex(media[1]);
  const theta1 = parseNumberInput(angleText);
  let body = <Notice>{strings.enterAll}</Notice>;
  if (n1 !== null && n2 !== null && theta1 !== null) {
    const check = solveSnell("theta2", { n1, n2, theta1 });
    body =
      !check.ok && check.error !== "no-solution" ? (
        <ErrorNotice>{errorText(check.error, strings)}</ErrorNotice>
      ) : (
        <RefractionResults
          fmt={fmt}
          n1={n1}
          n2={n2}
          sig={sig}
          strings={strings}
          theta1={theta1}
        />
      );
  }
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <MediumField
          fmt={fmt}
          id="snell-m1"
          label={strings.medium1}
          medium={media[0]}
          onChange={(next) => setMedia([next, media[1]])}
          strings={strings}
        />
        <MediumField
          fmt={fmt}
          id="snell-m2"
          label={strings.medium2}
          medium={media[1]}
          onChange={(next) => setMedia([media[0], next])}
          strings={strings}
        />
      </div>
      <div className="max-w-xs">
        <TextField
          id="snell-theta1"
          label={strings.incidenceInput}
          numeric
          onChange={setAngleText}
          value={angleText}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

function closestMaterial(n: number): MaterialId {
  return MATERIAL_ORDER.reduce((best, id) =>
    Math.abs(MATERIAL_INDEX[id] - n) < Math.abs(MATERIAL_INDEX[best] - n)
      ? id
      : best
  );
}

const UNKNOWN_FIELDS: Record<
  SnellTarget,
  readonly ("n1" | "n2" | "theta1" | "theta2")[]
> = {
  theta2: ["n1", "n2", "theta1"],
  theta1: ["n1", "n2", "theta2"],
  n2: ["n1", "theta1", "theta2"],
  n1: ["n2", "theta1", "theta2"],
};

export function UnknownPanel({ strings, fmt, sig }: PanelProps) {
  const [target, setTarget] = useState<SnellTarget>("n2");
  const [media, setMedia] = useState<[MediumState, MediumState]>([
    { id: "air", custom: "1" },
    { id: "custom", custom: "1.5" },
  ]);
  const [angles, setAngles] = useState({ theta1: "40", theta2: "25" });
  const angle = angleFormatter(fmt, sig);
  const fields = UNKNOWN_FIELDS[target];
  const known = {
    n1: mediumIndex(media[0]) ?? undefined,
    n2: mediumIndex(media[1]) ?? undefined,
    theta1: parseNumberInput(angles.theta1) ?? undefined,
    theta2: parseNumberInput(angles.theta2) ?? undefined,
  };
  const missing = fields.some((key) => known[key] === undefined);
  const outcome = missing ? null : solveSnell(target, known);
  const isAngle = target === "theta1" || target === "theta2";
  const result = outcome?.ok ? outcome.value : null;
  let shown = "";
  if (result !== null) {
    shown = isAngle ? angle(result) : formatSig(fmt, result, sig);
  }
  const mediaIndex = (key: "n1" | "n2") => (key === "n1" ? 0 : 1);

  return (
    <>
      <PillGroup
        label={strings.targetLabel}
        name="snell-target"
        onChange={setTarget}
        options={(Object.keys(strings.targets) as SnellTarget[]).map(
          (value) => ({
            value,
            label: strings.targets[value],
          })
        )}
        value={target}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map((key) =>
          key === "n1" || key === "n2" ? (
            <MediumField
              fmt={fmt}
              id={`snell-u-${key}`}
              key={key}
              label={key === "n1" ? strings.medium1 : strings.medium2}
              medium={media[mediaIndex(key)]}
              onChange={(next) =>
                setMedia(key === "n1" ? [next, media[1]] : [media[0], next])
              }
              strings={strings}
            />
          ) : (
            <TextField
              id={`snell-u-${key}`}
              key={key}
              label={
                key === "theta1"
                  ? strings.incidenceInput
                  : strings.refractionInput
              }
              numeric
              onChange={(value) => setAngles({ ...angles, [key]: value })}
              value={angles[key]}
            />
          )
        )}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {missing ? <Notice>{strings.enterAll}</Notice> : null}
        {outcome !== null && !outcome.ok ? (
          <ErrorNotice>{errorText(outcome.error, strings)}</ErrorNotice>
        ) : null}
        {result === null ? null : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.targets[target]}
                value={shown}
              />
              {isAngle ? null : (
                <StatCard
                  label={strings.closest}
                  value={t(strings.closestValue, {
                    name: strings.materials[closestMaterial(result)],
                    n: fmt(MATERIAL_INDEX[closestMaterial(result)], {
                      maximumFractionDigits: 6,
                    }),
                  })}
                />
              )}
            </StatGrid>
            <WorkingSteps
              heading={strings.working}
              lines={[
                t(strings.stepSolve, {
                  target: strings.targets[target],
                  result: shown,
                }),
              ]}
            />
          </>
        )}
      </section>
    </>
  );
}

export function CriticalPanel({ strings, fmt, sig }: PanelProps) {
  const [media, setMedia] = useState<[MediumState, MediumState]>([
    { id: "water", custom: "1.4" },
    { id: "air", custom: "1" },
  ]);
  const angle = angleFormatter(fmt, sig);
  const n1 = mediumIndex(media[0]);
  const n2 = mediumIndex(media[1]);
  const valid = n1 !== null && n2 !== null && n1 >= 1 && n2 >= 1;
  const critical = valid ? criticalAngle(n1, n2) : null;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <MediumField
          fmt={fmt}
          id="snell-c1"
          label={strings.medium1}
          medium={media[0]}
          onChange={(next) => setMedia([next, media[1]])}
          strings={strings}
        />
        <MediumField
          fmt={fmt}
          id="snell-c2"
          label={strings.medium2}
          medium={media[1]}
          onChange={(next) => setMedia([media[0], next])}
          strings={strings}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {valid ? null : <Notice>{strings.enterAll}</Notice>}
        {valid && critical === null ? (
          <Notice>{strings.noCriticalNote}</Notice>
        ) : null}
        {valid && critical !== null ? (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.critical}
                value={angle(critical)}
              />
              <StatCard
                label={strings.brewster}
                value={angle(brewsterAngle(n1, n2))}
              />
            </StatGrid>
            <RayDiagram
              labels={rayLabels(strings, n1, n2, critical, 90, fmt, sig)}
              n1={n1}
              n2={n2}
              theta1={critical}
              theta2={90}
            />
            <WorkingSteps
              heading={strings.working}
              lines={[
                t(strings.stepCritical, {
                  n1: formatSig(fmt, n1, sig),
                  n2: formatSig(fmt, n2, sig),
                  ratio: formatSig(fmt, n2 / n1, sig),
                  critical: angle(critical),
                }),
              ]}
            />
            <p className="text-muted-foreground text-sm">
              {strings.criticalNote}
            </p>
          </>
        ) : null}
      </section>
    </>
  );
}
